import { FilterOption } from "@/models/base/Filter";

export const MINISTRY_FILTER_KEY = "ministerium";

const compareMinistryNames = (a: string, b: string) =>
  a.localeCompare(b, "da", { sensitivity: "accent" });

/** Case-insensitive lookup for optionAliases keys (ASP.NET camelCases JSON dictionary keys). */
export function lookupAliasValues(
  currentName: string,
  optionAliases?: Record<string, string[]>
): string[] | undefined {
  if (!optionAliases) {
    return undefined;
  }

  if (optionAliases[currentName]) {
    return optionAliases[currentName];
  }

  const key = Object.keys(optionAliases).find(
    (k) => compareMinistryNames(k, currentName) === 0
  );
  return key ? optionAliases[key] : undefined;
}

/** Former names only (excludes the canonical current name). */
export function getFormerMinistryNames(
  currentName: string,
  optionAliases?: Record<string, string[]>
): string[] {
  const names = lookupAliasValues(currentName, optionAliases) ?? [currentName];
  return names.filter((n) => compareMinistryNames(n, currentName) !== 0);
}

/** Truncate alias list for inline display; full list available via tooltip. */
export function formatFormerNamesPreview(
  formerNames: string[],
  maxShown = 2
): { preview: string; hasMore: boolean; full: string } {
  if (formerNames.length === 0) {
    return { preview: "", hasMore: false, full: "" };
  }
  const full = formerNames.join(", ");
  if (formerNames.length <= maxShown) {
    return { preview: full, hasMore: false, full };
  }
  const preview = `${formerNames.slice(0, maxShown).join(", ")} +${formerNames.length - maxShown}`;
  return { preview, hasMore: true, full };
}

export function buildMinistryFilterOptions(
  options: string[],
  optionAliases?: Record<string, string[]>
): FilterOption[] {
  return options.map((current) => {
    const formerNames = getFormerMinistryNames(current, optionAliases);
    return {
      label: current,
      value: current,
      aliases: formerNames.length > 0 ? formerNames : undefined,
      searchKeywords: [current, ...formerNames],
    };
  });
}

/** Build filter options; applies ministry alias enrichment only for the ministerium filter. */
export function buildFilterOptions(
  filterKey: string,
  options: string[],
  optionAliases?: Record<string, string[]>
): FilterOption[] {
  if (filterKey === MINISTRY_FILTER_KEY && optionAliases) {
    return buildMinistryFilterOptions(options, optionAliases);
  }
  return options.map((opt) => ({
    label: String(opt),
    value: String(opt),
  }));
}

/**
 * Reverse lookup: given a raw ressort from document metadata, find the canonical
 * filter option name it groups under.
 */
export function resolveCurrentMinistryName(
  ressort: string,
  optionAliases?: Record<string, string[]>
): string | undefined {
  if (!ressort?.trim() || !optionAliases) {
    return undefined;
  }

  const normalized = ressort.trim();
  for (const [, aliases] of Object.entries(optionAliases)) {
    if (
      aliases.some((alias) => compareMinistryNames(alias, normalized) === 0)
    ) {
      // First entry is always the canonical current name from the directory.
      return aliases[0];
    }
  }

  return undefined;
}

/** Case-insensitive match of query against label or any search keyword / alias. */
export function filterOptionMatchesQuery(
  option: FilterOption,
  query: string
): { matches: boolean; matchedViaAlias?: string } {
  const q = query.trim().toLowerCase();
  if (!q) {
    return { matches: true };
  }

  if (option.label.toLowerCase().includes(q)) {
    return { matches: true };
  }

  const keywords = option.searchKeywords ?? option.aliases ?? [];
  for (const keyword of keywords) {
    if (keyword.toLowerCase().includes(q)) {
      const matchedViaAlias =
        keyword.toLowerCase() !== option.label.toLowerCase() ? keyword : undefined;
      return { matches: true, matchedViaAlias };
    }
  }

  return { matches: false };
}
